import React, { useState } from "react";
import {
  FormControl,
  FormLabel,
  Input,
  Button,
  Box,
  Text,
  InputGroup,
  InputRightElement,
  FormErrorMessage,
  SimpleGrid,
} from "@chakra-ui/react";
import { FiEye, FiEyeOff, FiCheck, FiX } from "react-icons/fi";
import UserCreateSuccessModal from "./UserCreateSuccessModal";
import AmplifyService from "../services/amplify";
import { useSearchParams } from "react-router-dom";
import { Image } from "@chakra-ui/react";
import { useAlert } from "./AlertProvider";
import { logError } from "./logger";

const ResetPassword: React.FC = () => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const [searchParams] = useSearchParams();
  const user = searchParams.get("user");
  const code = searchParams.get("code");
  const { showAlert } = useAlert();

  const [passwordCriteria, setPasswordCriteria] = useState({
    minLength: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });

  const validatePasswordCriteria = (password) => {
    setPasswordCriteria({
      minLength: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const handleClick = () => setPasswordVisible(!passwordVisible);

  const validatePassword = (password: string) => {
    // eslint-disable-next-line
    const regex = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[\^$*.{}\[\]()"?!@#%&/\\,><':;|_~`+=-]).{8,}$/;
    return regex.test(password);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      showAlert({
        severity: "error",
        title: "Whoops",
        message: "Passwords do not match"
      });
      return;
    }

    if (!validatePassword(password)) {
      setPasswordError(
        "Password must contain at least 8 characters, including an uppercase letter, a lowercase letter, a number, and a special character."
      );
      return;
    }

    // Validate that we have the required URL parameters
    if (!user || !code) {
      showAlert({
        severity: "error",
        title: "Invalid Reset Link",
        message: "The reset link is invalid or has expired. Please request a new password reset."
      });
      return;
    }

    try {
      const authService = new AmplifyService();
      const result = await authService.forgotPasswordSubmit(user, code, password);
      console.log(result);
      setSuccessModalOpen(true);
    } catch (error: any) {
      console.error("Error updating user:", error);
      
      let errorMessage = "An unexpected error occurred. Please try requesting a new password reset.";
      let shouldLogToSentry = true; // Flag to determine if we should log to Sentry
      
      // Handle specific AWS Cognito errors
      if (error.code) {
        switch (error.code) {
          case "CodeMismatchException":
            errorMessage = "Your reset link is incorrect. Please check your email and try again.";
            shouldLogToSentry = false; // Don't log legitimate user errors
            break;
          case "ExpiredCodeException":
            errorMessage = "Your reset link has expired. Please request a new password reset from the login page.";
            shouldLogToSentry = false; // Don't log legitimate user errors
            break;
          case "InvalidPasswordException":
            errorMessage = "The password does not meet the requirements. Please check the password criteria below.";
            shouldLogToSentry = false; // Don't log legitimate user errors
            break;
          case "LimitExceededException":
            errorMessage = "You have made too many recent attempts to reset this password. Please request a new password reset from the login page.";
            shouldLogToSentry = false; // Don't log legitimate user errors
            break;
          case "UserNotFoundException":
            errorMessage = "We couldn't find your account. Please check the reset link or request a new password reset from the login page.";
            shouldLogToSentry = false; // Don't log legitimate user errors
            break;
          case "InvalidParameterException":
            errorMessage = "Something went wrong. Please try requesting a new password reset.";
            shouldLogToSentry = false; // Don't log legitimate user errors
            break;
          default:
            errorMessage = `Error: ${error.message || error.code}`;
            // shouldLogToSentry remains true for unexpected errors
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Only log to Sentry if it's an unexpected error
      if (shouldLogToSentry) {
        logError("Error updating user", error);
      }
      
      showAlert({
        severity: "error",
        title: "Password Reset Failed",
        message: errorMessage
      });
    }
  };

  const handleSuccessModalClose = () => {
    setSuccessModalOpen(false);
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bg="#F7F9FC" 
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        bg="#FFFFFF"
        border="1px solid #E2E8F0"
        borderRadius="8px"
        boxShadow="md"
        p={'40px'}
        maxWidth="400px"
        width="100%" // Ensures responsiveness on smaller screens
        mx="16px" // Adds horizontal padding on smaller screens
      >
        <Image
          src="logo.jpg" 
          alt="Family Doctor Logo"
          mb={4}
          width="120px"
        />

        <Text fontSize="lg" fontWeight="bold" textAlign="center" mb={2}>
          Reset your password
        </Text>
        <Text fontSize="12px" color="gray.600" textAlign="center" mb={6}>
          Enter and confirm a new password for your account.
        </Text>

        <form onSubmit={onSubmit} style={{ width: "100%", marginTop: '10px' }}>
          <Input type="hidden" autoComplete="user" value={user} />

          <FormControl mb={4} isInvalid={passwordError !== ""}>
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
              New Password *
            </FormLabel>
            <InputGroup size="md">
              <Input
                pr="4.5rem"
                type={passwordVisible ? "text" : "password"}
                placeholder="New Password"
                borderRadius="30px"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError("");
                  validatePasswordCriteria(e.target.value);
                }}
                autoComplete="new-password"
                fontSize="sm"
              />
              <InputRightElement width="4.5rem">
                <Button h="1.75rem" size="sm" onClick={handleClick} borderRadius="30px">
                  {passwordVisible ? <FiEyeOff /> : <FiEye />}
                </Button>
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{passwordError}</FormErrorMessage>
          </FormControl>

          <FormControl mb={4}>
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
              Confirm New Password *
            </FormLabel>
            <InputGroup size="md">
              <Input
                pr="4.5rem"
                type={passwordVisible ? "text" : "password"}
                placeholder="Confirm your password"
                borderRadius="30px"
                value={confirmPassword}
                onChange={(e) => {setConfirmPassword(e.target.value);
                  validatePasswordCriteria(e.target.value);}}
                autoComplete="new-password"
                fontSize="sm"
              />
              <InputRightElement width="4.5rem">
                <Button h="1.75rem" size="sm" onClick={handleClick} borderRadius="30px">
                  {passwordVisible ? <FiEyeOff /> : <FiEye />}
                </Button>
              </InputRightElement>
            </InputGroup>
          </FormControl>

          <Box mt={4}>
                    <Text fontSize="sm" mb={2}>New password must contain:</Text>
                    <SimpleGrid columns={1} spacing={2}>
                      <Text display="flex" alignItems="center">
                        <Box as={passwordCriteria.minLength ? FiCheck : FiX} color={passwordCriteria.minLength ? "green.500" : "red.500"} />
                        <Text fontSize="sm" ml={2}>At least 8 characters</Text>
                      </Text>
                      <Text display="flex" alignItems="center">
                        <Box as={passwordCriteria.uppercase ? FiCheck : FiX} color={passwordCriteria.uppercase ? "green.500" : "red.500"} />
                        <Text fontSize="sm" ml={2}>An uppercase letter</Text>
                      </Text>
                      <Text display="flex" alignItems="center">
                        <Box as={passwordCriteria.lowercase ? FiCheck : FiX} color={passwordCriteria.lowercase ? "green.500" : "red.500"} />
                        <Text fontSize="sm" ml={2}>A lowercase letter</Text>
                      </Text>
                      <Text display="flex" alignItems="center">
                        <Box as={passwordCriteria.number ? FiCheck : FiX} color={passwordCriteria.number ? "green.500" : "red.500"} />
                        <Text fontSize="sm" ml={2}>A number</Text>
                      </Text>
                      <Text display="flex" alignItems="center">
                        <Box as={passwordCriteria.specialChar ? FiCheck : FiX} color={passwordCriteria.specialChar ? "green.500" : "red.500"} />
                        <Text fontSize="sm" ml={2}>A special character</Text>
                      </Text>
                    </SimpleGrid>
                  </Box>

                  {password !== confirmPassword && confirmPassword && (
                    <Text mt={4} color="red.500" fontSize="sm" fontWeight="bold">
                      New password and Confirm password do not match.
                    </Text>
                  )}

          <Text fontSize="12px" color="gray.500" textAlign="center" mb={4} mt={10}>
            Note: New password must contain at least 8 characters, including an uppercase letter, a lowercase letter, a number, and a special character.
          </Text>

          <Button
            bg="#B92F2F"
            color="#fff"
            mt={4}
            width="100%"
            borderRadius="24px"
            type="submit"
            py={6}
            fontSize="sm"
            _hover={{ bg: "#9a2727" }}
          >
            Reset Password
          </Button>
        </form>

        <UserCreateSuccessModal
          isOpen={isSuccessModalOpen}
          onClose={handleSuccessModalClose}
        />
      </Box>
    </Box>
  );
};

export default ResetPassword;
